import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, BarChart3, RefreshCw, Download, Activity, ShieldCheck, Zap, Package, Users, ClipboardList, Server, FileText, Percent } from "lucide-react";
import TdsReport from "@/pages/TdsReport";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "mtd", label: "Month to date" },
  { key: "ytd", label: "Year to date" },
  { key: "all", label: "All time" },
];

const fmt = (n) => (n === null || n === undefined ? "—" : (typeof n === "number" ? n.toLocaleString("en-IN") : String(n)));
const inr = (n) => (n === null || n === undefined ? "₹0" : new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0));

function KV({ label, value, tone = "default", testid }) {
  const toneCls = tone === "warn" ? "border-amber-200 bg-amber-50" : tone === "danger" ? "border-rose-200 bg-rose-50" : tone === "good" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}

function useAnalytics(path, period) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchIt = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(path, { params: { period } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed: ${path}`);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchIt(); }, [path, period]);
  return { data, loading, refresh: fetchIt };
}

function ExecutivePanel({ period }) {
  const { data, loading, refresh } = useAnalytics("/analytics/executive", period);
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Executive dashboard</CardTitle>
        <Button size="sm" variant="outline" onClick={refresh} data-testid="analytics-refresh-executive">
          {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Refresh
        </Button>
      </CardHeader>
      <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-executive">
        <KV label="Revenue (period)" value={inr(data?.revenue?.period)} tone="good" testid="metric-revenue-period" />
        <KV label="Revenue today" value={inr(data?.revenue?.today)} tone="good" />
        <KV label="Revenue (all time)" value={inr(data?.revenue?.total_all_time)} />
        <KV label="Active members" value={fmt(data?.members?.active)} testid="metric-active-members" />
        <KV label="New registrations" value={fmt(data?.members?.new_registrations)} />
        <KV label="Orders today" value={fmt(data?.orders?.today)} />
        <KV label="Medicine orders" value={fmt(data?.orders?.medicine)} />
        <KV label="Lab bookings" value={fmt(data?.orders?.lab)} />
        <KV label="Full body checkups" value={fmt(data?.orders?.fbc)} />
        <KV label="Pending quotations" value={fmt(data?.pending?.quotations)} tone="warn" />
        <KV label="Pending payments" value={fmt(data?.pending?.payments)} tone="warn" />
        <KV label="Pending dispatches" value={fmt(data?.pending?.dispatches)} tone="warn" />
        <KV label="Complaints" value={fmt(data?.support?.complaints)} tone="warn" />
        <KV label="Open tickets" value={fmt(data?.support?.open_tickets)} tone="warn" />
        <KV label="Failed payments" value={fmt(data?.exceptions?.failed_payments)} tone="danger" />
        <KV label="Delivery exceptions" value={fmt(data?.exceptions?.delivery_exceptions)} tone="danger" />
      </CardContent>
    </Card>
  );
}

function GridPanel({ title, path, period, mapper, testid }) {
  const { data, loading, refresh } = useAnalytics(path, period);
  const items = mapper(data);
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">{title}</CardTitle>
        <Button size="sm" variant="outline" onClick={refresh}>
          {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Refresh
        </Button>
      </CardHeader>
      <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={testid}>
        {items.map((it) => <KV key={it.label} {...it} />)}
      </CardContent>
    </Card>
  );
}

function ReportsPanel({ period }) {
  const [reports, setReports] = useState([]);
  useEffect(() => { api.get("/analytics/reports").then(({ data }) => setReports(data.reports || [])).catch(() => {}); }, []);
  const download = async (name) => {
    try {
      const r = await api.get(`/analytics/reports/${name}`, { params: { format: "csv", period }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${name}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-lg">Reports</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="analytics-reports">
        {reports.map((r) => (
          <Button key={r} variant="outline" onClick={() => download(r)} data-testid={`report-${r}`}>
            <Download size={14} /> {r}
          </Button>
        ))}
      </CardContent>
    </Card>
  );
}

function SystemHealthPanel() {
  const [data, setData] = useState(null);
  const [prod, setProd] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/analytics/system-health"),
        api.get("/analytics/production-readiness"),
      ]);
      setData(a.data); setProd(b.data);
    } catch (e) { toast.error("Health check failed"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">System health</CardTitle>
          <Button size="sm" variant="outline" onClick={refresh}>
            {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Refresh
          </Button>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-system-health">
          <KV label="API" value={data?.api?.ok ? "OK" : "DOWN"} tone={data?.api?.ok ? "good" : "danger"} />
          <KV label="DB (rtt)" value={data?.database?.ok ? `${data?.database?.rtt_ms} ms` : "DOWN"} tone={data?.database?.ok ? "good" : "danger"} />
          <KV label="Collections" value={fmt(data?.database?.collections)} />
          <KV label="Storage used" value={data?.storage?.used_pct != null ? `${data.storage.used_pct}%` : "—"} />
          <KV label="Memory used" value={data?.resources?.memory_used_pct != null ? `${data.resources.memory_used_pct}%` : "—"} />
          <KV label="Load 1m" value={fmt(data?.resources?.load_1m)} />
          <KV label="Notif queued" value={fmt(data?.queue?.notification_queued)} />
          <KV label="Notif failed" value={fmt(data?.queue?.notification_failed)} tone="warn" />
          <KV label="Exception queue" value={fmt(data?.queue?.exception_queue_open)} tone="warn" />
          <KV label="Webhook success" value={data?.webhook_success_rate?.success_rate != null ? `${Math.round((data.webhook_success_rate.success_rate || 0) * 100)}%` : "—"} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-lg">Production readiness</CardTitle></CardHeader>
        <CardContent className="space-y-3" data-testid="analytics-production-readiness">
          <div className="flex items-center gap-2">
            <Badge className={prod?.overall_ok ? "bg-emerald-100 text-emerald-800 border-emerald-200 border" : "bg-rose-100 text-rose-800 border-rose-200 border"}>
              {prod?.overall_ok ? "READY" : "NOT READY"}
            </Badge>
            <span className="text-sm text-slate-500">Aggregate go/no-go</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KV label="Env missing" value={fmt(prod?.environment?.missing?.length || 0)} tone={prod?.environment?.ok ? "good" : "danger"} />
            <KV label="Collections missing" value={fmt(prod?.collections?.missing?.length || 0)} tone={prod?.collections?.ok ? "good" : "danger"} />
            <KV label="Index gaps" value={fmt((prod?.indexes?.per_collection || []).filter((c) => !c.ok).length)} tone={prod?.indexes?.ok ? "good" : "warn"} />
            <KV label="Orphan orders" value={fmt(prod?.orphan_data?.count)} tone={prod?.orphan_data?.count ? "warn" : "good"} />
            <KV label="Duplicate members" value={fmt(prod?.duplicate_data?.count)} tone={prod?.duplicate_data?.count ? "warn" : "good"} />
            <KV label="Audit events" value={fmt(prod?.audit?.total_audit_events)} />
            <KV label="RBAC enforce" value={prod?.security?.rbac_enforce || "0"} />
            <KV label="Wildcard admin" value={prod?.security?.wildcard_admin_allowlist ? "YES" : "no"} tone={prod?.security?.wildcard_admin_allowlist ? "danger" : "good"} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Analytics() {
  const [period, setPeriod] = useState("30d");
  return (
    <div className="p-6 space-y-4" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><BarChart3 /> Analytics &amp; BI</h1>
          <p className="text-sm text-slate-500">Business intelligence, financial analytics, operations, system health, reports, and production-readiness checks.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44" data-testid="analytics-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="executive" className="space-y-3">
        <TabsList className="flex-wrap">
          <TabsTrigger value="executive" data-testid="tab-executive"><Activity size={14} /> Executive</TabsTrigger>
          <TabsTrigger value="financial" data-testid="tab-financial"><Zap size={14} /> Financial</TabsTrigger>
          <TabsTrigger value="membership" data-testid="tab-membership"><Users size={14} /> Membership</TabsTrigger>
          <TabsTrigger value="medicine" data-testid="tab-medicine"><Package size={14} /> Medicine</TabsTrigger>
          <TabsTrigger value="lab" data-testid="tab-lab"><ClipboardList size={14} /> Lab &amp; FBC</TabsTrigger>
          <TabsTrigger value="customer" data-testid="tab-customer"><Users size={14} /> Customer</TabsTrigger>
          <TabsTrigger value="operations" data-testid="tab-operations"><Server size={14} /> Operations</TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health"><ShieldCheck size={14} /> Health &amp; Prod</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports"><FileText size={14} /> Reports</TabsTrigger>
          <TabsTrigger value="tds" data-testid="tab-tds"><Percent size={14} /> TDS</TabsTrigger>
        </TabsList>

        <TabsContent value="executive"><ExecutivePanel period={period} /></TabsContent>

        <TabsContent value="financial">
          <GridPanel title="Financial analytics" path="/analytics/financial" period={period} testid="analytics-financial"
            mapper={(d) => [
              { label: "Sales revenue", value: inr(d?.sales_revenue), tone: "good" },
              { label: "Purchase cost", value: inr(d?.purchase_cost) },
              { label: "Gross profit", value: inr(d?.gross_profit), tone: "good" },
              { label: "Net profit", value: inr(d?.net_profit), tone: "good" },
              { label: "Receivables", value: inr(d?.outstanding?.receivables), tone: "warn" },
              { label: "Payables", value: inr(d?.outstanding?.payables), tone: "warn" },
              { label: "GST total", value: inr(d?.gst?.total) },
              { label: "Cashfree collections", value: inr(d?.cashfree_collections) },
              { label: "Refunds", value: inr(d?.refunds), tone: "warn" },
              { label: "Vendor payments", value: inr(d?.payments?.vendor) },
              { label: "Customer payments", value: inr(d?.payments?.customer) },
            ]} />
        </TabsContent>

        <TabsContent value="membership">
          <GridPanel title="Membership analytics" path="/analytics/membership" period={period} testid="analytics-membership"
            mapper={(d) => [
              { label: "New members", value: fmt(d?.new_members), tone: "good" },
              { label: "Renewals", value: fmt(d?.renewals) },
              { label: "BK teachers", value: fmt(d?.bk_teacher_vs_regular?.bk_teacher) },
              { label: "Regular members", value: fmt(d?.bk_teacher_vs_regular?.regular) },
              { label: "Inactive", value: fmt(d?.inactive_members), tone: "warn" },
            ]} />
        </TabsContent>

        <TabsContent value="medicine">
          <GridPanel title="Medicine analytics" path="/analytics/medicine" period={period} testid="analytics-medicine"
            mapper={(d) => [
              { label: "Medicine orders", value: fmt(d?.medicine_orders) },
              { label: "Avg order value", value: inr(d?.average_order_value) },
              { label: "Quotation conversion", value: d?.quotation_conversion_rate != null ? `${Math.round((d.quotation_conversion_rate || 0) * 100)}%` : "—", tone: "good" },
              { label: "Cancelled", value: fmt(d?.cancelled_orders), tone: "warn" },
              { label: "Rejected Rx", value: fmt(d?.rejected_prescriptions), tone: "warn" },
              { label: "Rx success rate", value: d?.prescription_ai?.success_rate != null ? `${Math.round((d.prescription_ai.success_rate || 0) * 100)}%` : "—" },
              { label: "Human review %", value: d?.prescription_ai?.human_review_pct != null ? `${Math.round((d.prescription_ai.human_review_pct || 0) * 100)}%` : "—" },
              { label: "Duplicate Rx %", value: d?.prescription_ai?.duplicate_pct != null ? `${Math.round((d.prescription_ai.duplicate_pct || 0) * 100)}%` : "—" },
            ]} />
        </TabsContent>

        <TabsContent value="lab">
          <GridPanel title="Lab & Full-body checkups" path="/analytics/lab" period={period} testid="analytics-lab"
            mapper={(d) => [
              { label: "Lab bookings", value: fmt(d?.lab_bookings) },
              { label: "Completed", value: fmt(d?.completed_tests), tone: "good" },
              { label: "Cancelled", value: fmt(d?.cancelled_bookings), tone: "warn" },
              { label: "Pending reports", value: fmt(d?.pending_reports), tone: "warn" },
              { label: "Home collection", value: fmt(d?.home_vs_center?.home_collection) },
              { label: "Center visit", value: fmt(d?.home_vs_center?.center_visit) },
            ]} />
        </TabsContent>

        <TabsContent value="customer">
          <GridPanel title="Customer analytics" path="/analytics/customer" period={period} testid="analytics-customer"
            mapper={(d) => [
              { label: "New customers", value: fmt(d?.new_customers), tone: "good" },
              { label: "Returning", value: fmt(d?.returning_customers) },
              { label: "LTV avg", value: inr(d?.customer_lifetime_value_avg) },
              { label: "Repeat order %", value: d?.repeat_order_pct != null ? `${Math.round((d.repeat_order_pct || 0) * 100)}%` : "—" },
              { label: "Complaint rate", value: d?.complaint_rate != null ? `${(d.complaint_rate * 100).toFixed(2)}%` : "—" },
              { label: "Avg resolution (h)", value: fmt(d?.average_resolution_hours) },
              { label: "CSAT (5)", value: fmt(d?.customer_satisfaction_avg) },
              { label: "NPS", value: fmt(d?.nps) },
            ]} />
        </TabsContent>

        <TabsContent value="operations">
          <GridPanel title="Operations" path="/analytics/operations" period={period} testid="analytics-operations"
            mapper={(d) => [
              { label: "Pending approvals", value: fmt(d?.pending_approvals), tone: "warn" },
              { label: "Awaiting purchase", value: fmt(d?.orders_awaiting_purchase), tone: "warn" },
              { label: "Purchase delays", value: fmt(d?.purchase_delays), tone: "warn" },
              { label: "Dispatch queue", value: fmt(d?.dispatch_queue), tone: "warn" },
              { label: "Delivery delays", value: fmt(d?.delivery_delays), tone: "warn" },
              { label: "Exception queue", value: fmt(d?.exception_queue), tone: "danger" },
              { label: "Failed OCR jobs", value: fmt(d?.failed_ocr_jobs), tone: "warn" },
              { label: "Failed WhatsApp", value: fmt(d?.failed_whatsapp_messages), tone: "warn" },
              { label: "Failed AI requests", value: fmt(d?.failed_ai_requests), tone: "warn" },
            ]} />
        </TabsContent>

        <TabsContent value="health"><SystemHealthPanel /></TabsContent>

        <TabsContent value="reports"><ReportsPanel period={period} /></TabsContent>

        <TabsContent value="tds" className="mt-0">
          {/* Embedded TDS Report — Form-26AS style export for BK-teacher orders */}
          <div className="-mx-6 -mt-3"><TdsReport /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
